import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from "react";
import { useTranslation } from "react-i18next";
import { BlogGrid, BlogTitle } from "./styles";
import { CardBlog } from "./CardBlog";
import { HeaderSEO } from "components/headerSEO";
import { ISeo } from "interfaces/intarfaces";
import { useNavigate } from "react-router-dom";
import { ContentDiv } from "components/containers";
import { getBlogData } from "../../../utils/getBlogData";
import styled from "styled-components/macro";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { OrangePlayFull } from "../../../components/Icons/OrangePlayFull";
import { Input, Button, Select } from "antd";
import { useIsMobile } from "../../../utils/hooks/useWindowSize";

interface IPost {
  title: string;
  url: string;
  post: number;
  text: string[];
  tags: string[];
}

const FilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 20px; // This adds some space between the items
  flex-wrap: wrap; // This allows the items to wrap to the next line if they don't fit.
  margin-bottom: 1em !important;
  padding: 0 1em;
  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

// const SpanDiv = styled.div`
//   margin-bottom:0.3em;
//   `

export const Blog: React.FC = () => {
  const { t, i18n } = useTranslation("blog");

  let inputSize = "medium" as SizeType;
  const [myposts, setMyPosts] = useState<IPost[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const allTags = Array.from(new Set(myposts.flatMap((post) => post.tags)));
  const navigate = useNavigate();

  const seo = t("blog.seo", { returnObjects: true }) as ISeo;
  const isMobile = useIsMobile();

  const [textWidth, setTextWidth] = useState<string>("376px");

  useEffect(() => {
    if (isMobile) {
      setTextWidth("250px");
    } else {
      setTextWidth("376px");
    }
  }, [isMobile]);

  useEffect(() => {
    async function addResourceBundle() {
      const blogData = getBlogData(i18n.language);
      const myPosts = blogData
        .sort((a, b) => a.post - b.post) // Sort the blogData based on post number
        .reverse();
      setMyPosts(myPosts);
    }
    addResourceBundle();
  }, [i18n.language]);

  const handleNavigate = useCallback(() => {
    navigate({ search: "?f=f" }, { replace: true });
  }, [navigate]);

  useEffect(() => {
    myposts.length && handleNavigate();
  }, [handleNavigate, myposts]);

  const options = allTags.map((tag) => ({
    label: tag,
    value: tag
  }));

  const handleChange = (value: string[] | string) => {
    setSelectedTags(value as string[]);
  };

  function filterPosts(posts: IPost[]): IPost[] {
    return posts.filter((post: IPost) => {
      const matchesSearchTerm =
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (Array.isArray(post.text) &&
          post.text.some((paragraph) =>
            paragraph.toLowerCase().includes(searchTerm.toLowerCase())
          ));

      let hasSelectedTags = false;

      // If there's only 1 tag selected
      if (selectedTags.length === 1) {
        hasSelectedTags = post.tags.includes(selectedTags[0]);
      }
      // If there are 2 or more tags selected
      else if (selectedTags.length > 1) {
        hasSelectedTags = selectedTags.every((tag) => post.tags.includes(tag));
      }
      // If no tags are selected
      else {
        hasSelectedTags = true;
      }
      if (searchTerm && !matchesSearchTerm) return false;
      if (selectedTags.length && !hasSelectedTags) return false;

      return true;
    });
  }

  const displayedPosts = useMemo(
    () => filterPosts(myposts),
    [myposts, searchTerm, selectedTags]
  );

  return (
    <ContentDiv>
      {seo && <HeaderSEO {...seo} />}
      <BlogTitle>
        <div>
          {t("blog.title.1")}
          <span> {t("blog.title.2")}</span>
        </div>
        {/* <OrangePlayFull viewView={textWidth} viewHeight="16px" marginBottom="0.3em" strokeWidth="8" /> */}
      </BlogTitle>
      <FilterContainer>
        <Input
          type="text"
          placeholder={t("blog.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 2 }}
        ></Input>
        <Select
          mode="multiple"
          size={inputSize}
          placeholder={t("blog.tagsPlaceholder")}
          value={selectedTags}
          onChange={handleChange}
          style={{ width: "100%", flex: 2 }}
          options={options}
        />
        <Button onClick={() => setSelectedTags([])}>
          {t("blog.clearTags")}
        </Button>
      </FilterContainer>
      <BlogGrid>
        {displayedPosts.map((item: IPost, j: number) => (
          <CardBlog keyPost={item.url} index={j} key={j} />
        ))}
      </BlogGrid>
      <div style={{ height: 1, background: "white" }} />
    </ContentDiv>
  );
};
